import { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import contentEs from './espanol.json';
import contentEn from './english.json';
import contentPt from './portugues.json';
import whatsappLogo from './imagenes/logo-whatsapp.webp';
import cerroCurrumahuida from './imagenes/postales_de_nuestro_entorno/Cerro_Currumahuida.webp';
import cajonDelAzul from './imagenes/postales_de_nuestro_entorno/cajon_del_azul.webp';
import feriaArtesanos from './imagenes/postales_de_nuestro_entorno/feria_artesanos.webp';
import laTrochita from './imagenes/postales_de_nuestro_entorno/la_trochita.webp';
import lagoPuelo from './imagenes/postales_de_nuestro_entorno/lago_puelo.webp';

const galleryFolders = {
  cabanaCuatro: require.context('./imagenes/cabaña_cuatro_personas', false, /\.webp$/),
  cabanaOcho: require.context('./imagenes/cabaña_ocho_personas', false, /\.webp$/),
  hostel: require.context('./imagenes/hostel', false, /\.webp$/),
  bicicletas: require.context('./imagenes/alquiler_bicicletas', false, /\.webp$/),
  kayak: require.context('./imagenes/alquiler_kayak', false, /\.webp$/),
};


const formatGalleryImages = (context) =>
  context.keys().map((key) => ({
    src: context(key),
    filename: key.replace('./', ''),
  }));

const entornoImageSources = [lagoPuelo, cerroCurrumahuida, cajonDelAzul, feriaArtesanos, laTrochita];

const contentByLanguage = {
  es: contentEs,
  en: contentEn,
  pt: contentPt,
};
const whatsappUrl = 'https://wa.me/5492944935850';

const formatText = (template = '', values = {}) =>
  template.replace(/\{(\w+)\}/g, (_, key) => (values[key] !== undefined ? values[key] : ''));

const getSavedLanguage = () => {
  const savedPreference = window.localStorage.getItem('preferredLanguage');
  if (savedPreference && contentByLanguage[savedPreference]) {
    return savedPreference;
  }
  return null;
};

const getBrowserLanguage = () => {
  const browserLanguage = navigator.language?.toLowerCase() ?? '';
  if (browserLanguage.startsWith('es')) {
    return 'es';
  }
  if (browserLanguage.startsWith('pt')) {
    return 'pt';
  }
  if (browserLanguage.startsWith('en')) {
    return 'en';
  }

  return 'es';
};

const getInitialLanguage = () => getSavedLanguage() ?? getBrowserLanguage();

const getLanguageByCountry = (countryCode = '') => {
  const normalized = countryCode.toUpperCase();
  const spanishCountries = new Set([
    'AR',
    'BO',
    'CL',
    'CO',
    'CR',
    'CU',
    'DO',
    'EC',
    'ES',
    'GT',
    'HN',
    'MX',
    'NI',
    'PA',
    'PE',
    'PR',
    'PY',
    'SV',
    'UY',
    'VE',
  ]);
  const portugueseCountries = new Set(['AO', 'BR', 'CV', 'GW', 'MZ', 'PT', 'ST', 'TL']);
  const englishCountries = new Set(['AU', 'CA', 'GB', 'IE', 'NZ', 'US', 'ZA']);

  if (spanishCountries.has(normalized)) {
    return 'es';
  }
  if (portugueseCountries.has(normalized)) {
    return 'pt';
  }
  if (englishCountries.has(normalized)) {
    return 'en';
  }
  return null;
};

function App() {
  const [language, setLanguage] = useState(getInitialLanguage);
  const [hasSavedPreference, setHasSavedPreference] = useState(() => Boolean(getSavedLanguage()));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeGallery, setActiveGallery] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeCabinDetails, setActiveCabinDetails] = useState(null);
  const thumbsTrackRef = useRef(null);
  const geoLanguageAttemptedRef = useRef(false);
  const content = contentByLanguage[language] ?? contentEs;
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const isNotFound = normalizedPath !== '/' && normalizedPath !== '/index.html';

  useEffect(() => {
    window.localStorage.setItem('preferredLanguage', language);
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    if (hasSavedPreference || geoLanguageAttemptedRef.current) {
      return;
    }

    geoLanguageAttemptedRef.current = true;
    const detectLanguage = async () => {
      try {
        const response = await fetch('https://ipwho.is/');
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (data.success === false) {
          return;
        }
        const detectedLanguage = getLanguageByCountry(data.country_code ?? '');
        if (detectedLanguage && detectedLanguage !== language) {
          setLanguage(detectedLanguage);
        }
      } catch (error) {
        return;
      }
    };

    detectLanguage();
  }, [hasSavedPreference, language]);

  const galleryOrder = useMemo(
    () => [
      {
        id: 'cabanas-4',
        title: content.lodging.cabinOptions[0].name,
        folderKey: 'cabanaCuatro',
        group: 'lodging',
      },
      {
        id: 'cabanas-8',
        title: content.lodging.cabinOptions[1].name,
        folderKey: 'cabanaOcho',
        group: 'lodging',
      },
      {
        id: 'hostel',
        title: content.lodging.hostel.title,
        folderKey: 'hostel',
        group: 'lodging',
      },
      {
        id: 'bicicletas',
        title: content.services.items[0].title,
        folderKey: 'bicicletas',
        group: 'services',
      },
      { id: 'kayak', title: content.services.items[1].title, folderKey: 'kayak', group: 'services' },
    ],
    [content]
  );

  const galleryCollections = useMemo(
    () =>
      galleryOrder.map((gallery) => {
        const context = galleryFolders[gallery.folderKey];
        const images = formatGalleryImages(context);
        const featuredIndex = images.findIndex((image) => /principal/i.test(image.filename));
        const highlightedIndex = featuredIndex === -1 ? 0 : featuredIndex;
        return {
          ...gallery,
          images,
          featured: images[highlightedIndex],
          featuredIndex: highlightedIndex,
        };
      }),
    [galleryOrder]
  );

  const lodgingGalleries = useMemo(
    () => galleryCollections.filter((gallery) => gallery.group === 'lodging'),
    [galleryCollections]
  );

  const lodgingDetails = useMemo(
    () => ({
      'cabanas-4': {
        type: 'cabin',
        ...content.lodging.cabinOptions[0],
      },
      'cabanas-8': {
        type: 'cabin',
        ...content.lodging.cabinOptions[1],
      },
      hostel: {
        type: 'hostel',
        ...content.lodging.hostel,
      },
    }),
    [content]
  );

  const serviceGalleries = useMemo(
    () => galleryCollections.filter((gallery) => gallery.group === 'services'),
    [galleryCollections]
  );

  const serviceDetails = useMemo(
    () => ({
      bicicletas: {
        ...content.services.items[0],
        galleryId: 'bicicletas',
      },
      kayak: {
        ...content.services.items[1],
        galleryId: 'kayak',
      },
    }),
    [content]
  );

  const entornoImages = useMemo(
    () =>
      entornoImageSources.map((url, index) => ({
        title: content.gallery.highlights[index]?.title ?? '',
        url,
      })),
    [content]
  );

  const openGallery = (gallery) => {
    setActiveGallery(gallery);
    setActiveImageIndex(gallery.featuredIndex);
  };

  const closeGallery = () => {
    setActiveGallery(null);
    setActiveImageIndex(0);
  };

  useEffect(() => {
    if (!activeGallery) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeGallery();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeGallery]);

  useEffect(() => {
    if (!activeCabinDetails) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setActiveCabinDetails(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeCabinDetails]);

  useEffect(() => {
    if (!thumbsTrackRef.current) {
      return;
    }

    const activeThumb = thumbsTrackRef.current.querySelector('[data-thumb-active="true"]');
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeImageIndex, activeGallery]);

  const scrollThumbs = (direction) => {
    if (!thumbsTrackRef.current) {
      return;
    }

    const scrollAmount = 200 * direction;
    thumbsTrackRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const revealElements = Array.from(document.querySelectorAll('.reveal'));

    if (prefersReducedMotion) {
      revealElements.forEach((element) => element.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -10% 0px',
      }
    );

    revealElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [language]);

  const openCabinDetails = (cabinId) => {
    setActiveCabinDetails(cabinId);
  };

  const closeCabinDetails = () => {
    setActiveCabinDetails(null);
  };

  const languageOptions = [
    { value: 'es', label: content.labels.languageOptions.es },
    { value: 'en', label: content.labels.languageOptions.en },
    { value: 'pt', label: content.labels.languageOptions.pt },
  ];

  const handleLanguageChange = (event) => {
    setHasSavedPreference(true);
    setLanguage(event.target.value);
  };

  const handleMenuToggle = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleNavLinkClick = () => {
    setIsMenuOpen(false);
  };

  if (isNotFound) {
    return (
      <div className="app app--not-found">
        <main className="not-found">
          <div className="not-found__content">
            <p className="not-found__eyebrow">404</p>
            <h1>{content.notFound.title}</h1>
            <p>{content.notFound.description}</p>
            <a className="not-found__cta" href="/">
              {content.notFound.cta}
            </a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="hero" id="inicio">
        <nav className="hero__nav reveal">
          <span className="hero__brand">{content.brand}</span>
          <button
            className="hero__menu-toggle"
            type="button"
            aria-label={content.labels.openMenu}
            aria-expanded={isMenuOpen}
            onClick={handleMenuToggle}
          >
            <span className="hero__menu-icon" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
          <div className={`hero__nav-actions${isMenuOpen ? ' is-open' : ''}`}>
            <div className="hero__links">
              {content.navLinks.map((link) => (
                <a key={link.href} href={link.href} onClick={handleNavLinkClick}>
                  {link.label}
                </a>
              ))}
            </div>
            <div className="language-switch">
              <select
                id="language-select"
                value={language}
                onChange={handleLanguageChange}
                aria-label={content.labels.language}
              >
                {languageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </nav>
        <div className="hero__content reveal">
          <p className="hero__eyebrow">{content.hero.eyebrow}</p>
          <h1>{content.hero.title}</h1>
          <p className="hero__subtitle">{content.hero.subtitle}</p>
          <a className="hero__cta" href={content.hero.cta.href}>
            {content.hero.cta.label}
          </a>
        </div>
      </header>

      <main>
        <section className="section" id="alojamiento">
          <div className="section__header reveal">
            <p className="section__tag">{content.lodging.tag}</p>
            <h2>{content.lodging.title}</h2>
            <p>{content.lodging.description}</p>
          </div>
          <div className="lodging-grid">
            {lodgingGalleries.map((gallery) => {
              const details = lodgingDetails[gallery.id];
              const isHostel = details?.type === 'hostel';

              return (
                <article
                  className={`lodging-card reveal${isHostel ? ' lodging-card--hostel' : ''}`}
                  key={gallery.id}
                >
                  <button
                    type="button"
                    className="lodging-card__image"
                    onClick={() => openGallery(gallery)}
                  >
                    <img
                      src={gallery.featured?.src}
                      alt={formatText(content.labels.featuredImageAlt, { title: gallery.title })}
                      loading="lazy"
                    />
                  </button>
                  <div className="lodging-card__content">
                    <div>
                      <h3>{gallery.title}</h3>
                      <p className="lodging-card__count">
                        {formatText(content.labels.photoCount, { count: gallery.images.length })}
                      </p>
                      {details?.type === 'cabin' && (
                        <p className="card__capacity">{details.capacity}</p>
                      )}
                      <p>{details?.description}</p>
                    </div>
                    {details?.type === 'cabin' && (
                      <ul>
                        {details.services.map((service) => (
                          <li key={service}>{service}</li>
                        ))}
                      </ul>
                    )}
                    {details?.type === 'cabin' && details.details?.length > 0 && (
                      <div className="lodging-card__details">
                        <button
                          type="button"
                          className="lodging-card__details-button"
                          onClick={() => openCabinDetails(gallery.id)}
                        >
                          {content.labels.viewDetails}
                        </button>
                      </div>
                    )}
                    {details?.type === 'cabin' && details.bookingUrl && (
                      <div className="lodging-card__actions">
                        <a
                          className="lodging-card__button"
                          href={details.bookingUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {content.labels.bookNow}
                        </a>
                      </div>
                    )}
                    {details?.type === 'hostel' && (
                      <>
                        <p className="lodging-card__highlight">{details.highlight}</p>
                        <div className="lodging-card__actions">
                          <a
                            className="lodging-card__button"
                            href={whatsappUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {content.labels.whatsappInquiry}
                          </a>
                        </div>
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="section section--alt" id="servicios">
          <div className="section__header reveal">
            <p className="section__tag">{content.services.tag}</p>
            <h2>{content.services.title}</h2>
          </div>
          <div className="services-grid">
            {Object.values(serviceDetails).map((service) => {
              const gallery = service.galleryId
                ? serviceGalleries.find((item) => item.id === service.galleryId)
                : null;

              return (
                <article className="service-card reveal" key={service.title}>
                  {gallery ? (
                    <button
                      type="button"
                      className="service-card__image"
                      onClick={() => openGallery(gallery)}
                    >
                      <img
                        src={gallery.featured?.src}
                        alt={formatText(content.labels.featuredImageAlt, { title: gallery.title })}
                        loading="lazy"
                      />
                    </button>
                  ) : (
                    <div className="service-card__image service-card__image--placeholder">
                      <span>{service.title.split(' ')[0]}</span>
                    </div>
                  )}
                  <div className="service-card__content">
                    <h3>{service.title}</h3>
                    {gallery && (
                      <p className="service-card__count">
                        {formatText(content.labels.photoCount, { count: gallery.images.length })}
                      </p>
                    )}
                    <p>{service.description}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="section" id="opiniones">
          <div className="section__header reveal">
            <p className="section__tag">{content.testimonials.tag}</p>
            <h2>{content.testimonials.title}</h2>
            <div className="score">
              {typeof content.testimonials.score === 'string' ? (
                content.testimonials.score
              ) : (
                <>
                  <span>{content.testimonials.score.booking}</span>
                  <span>{content.testimonials.score.google}</span>
                </>
              )}
            </div>
            <div className="testimonials__actions">
              <a
                className="testimonials__button"
                href={content.testimonials.bookingUrl}
                target="_blank"
                rel="noreferrer"
              >
                {content.labels.bookingReviews}
              </a>
              <a
                className="testimonials__button testimonials__button--secondary"
                href={content.testimonials.googleUrl}
                target="_blank"
                rel="noreferrer"
              >
                {content.labels.googleReviews}
              </a>
            </div>
          </div>
          <div className="testimonials-carousel reveal">
            <div className="testimonials-carousel__track">
              {[...content.testimonials.items, ...content.testimonials.items].map(
                (testimonial, index) => (
                  <figure key={`${testimonial.author}-${index}`} className="testimonials-carousel__item">
                    <blockquote>“{testimonial.quote}”</blockquote>
                    <figcaption>
                      {testimonial.author}
                      {testimonial.source && <span className="testimonials-carousel__source">{testimonial.source}</span>}
                    </figcaption>
                  </figure>
                )
              )}
            </div>
          </div>
        </section>

        <section className="section section--alt" id="galeria">
          <div className="section__header reveal">
            <p className="section__tag">{content.gallery.tag}</p>
            <h2>{content.gallery.title}</h2>
          </div>
          <div className="carousel reveal">
            <div className="carousel__track">
              {[...entornoImages, ...entornoImages].map((image, index) => (
                <figure key={`${image.title}-${index}`} className="carousel__item">
                  <img src={image.url} alt={image.title} loading="lazy" />
                  <figcaption>{image.title}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="ubicacion">
          <div className="section__header reveal">
            <p className="section__tag">{content.location.tag}</p>
            <h2>{content.location.title}</h2>
            <p>{content.location.description}</p>
          </div>
          <div className="location reveal">
            <div>
              <h3>{content.location.howToTitle}</h3>
              <p>{content.location.howToDescription}</p>
              <ul>
                {content.location.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </div>
            <iframe
              title={content.location.map.title}
              src={content.location.map.src}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </section>
      </main>

      <footer className="section section--alt" id="contacto">
        <div className="section__header reveal">
          <p className="section__tag">{content.contact.tag}</p>
          <h2>{content.contact.title}</h2>
        </div>
        <div className="contact reveal">
          <div>
            <h3>{content.contact.talkTitle}</h3>
            <p>
              {content.contact.phoneLabel} <strong>{content.contact.phone}</strong>
            </p>
            <p>{content.contact.phoneNote}</p>
            <p>{content.contact.address}</p>
          </div>
          {/* <div className="contact__info">
            <p>{content.contact.followText}</p>
            <div className="contact__tags">
              {content.contact.socialTags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          </div> */}
        </div>
      </footer>

      <a
        className="whatsapp-float"
        href={whatsappUrl}
        target="_blank"
        rel="noreferrer"
        aria-label={content.labels.whatsappChat}
      >
        <img className="whatsapp-float__icon" src={whatsappLogo} alt="" aria-hidden="true" />
      </a>

      {activeGallery && (
        <div
          className="gallery-modal"
          role="dialog"
          aria-modal="true"
          aria-label={formatText(content.labels.galleryDialogLabel, { title: activeGallery.title })}
        >
          <div className="gallery-modal__backdrop" onClick={closeGallery} />
          <div className="gallery-modal__content">
            <header className="gallery-modal__header">
              <div>
                <p className="gallery-modal__eyebrow">{content.labels.galleryEyebrow}</p>
                <h3>{activeGallery.title}</h3>
              </div>
              <button type="button" className="gallery-modal__close" onClick={closeGallery}>
                {content.labels.close}
              </button>
            </header>
            <div className="gallery-modal__main">
              <img
                src={activeGallery.images[activeImageIndex]?.src}
                alt={formatText(content.labels.galleryPhotoAlt, {
                  title: activeGallery.title,
                  index: activeImageIndex + 1,
                })}
              />
            </div>
            <div className="gallery-modal__thumbs" aria-label={content.labels.galleryThumbnails}>
              <button
                type="button"
                className="gallery-modal__thumb-nav"
                aria-label={content.labels.previousThumbnails}
                onClick={() => scrollThumbs(-1)}
              >
                ‹
              </button>
              <div className="gallery-modal__thumbs-track" ref={thumbsTrackRef}>
                {activeGallery.images.map((image, index) => (
                  <button
                    key={image.filename}
                    type="button"
                    className={`gallery-modal__thumb${index === activeImageIndex ? ' is-active' : ''}`}
                    onClick={() => setActiveImageIndex(index)}
                    data-thumb-active={index === activeImageIndex}
                  >
                    <img
                      src={image.src}
                      alt={formatText(content.labels.thumbnailAlt, {
                        title: activeGallery.title,
                        index: index + 1,
                      })}
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="gallery-modal__thumb-nav"
                aria-label={content.labels.nextThumbnails}
                onClick={() => scrollThumbs(1)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}

      {activeCabinDetails && (
        <div
          className="details-modal"
          role="dialog"
          aria-modal="true"
          aria-label={formatText(content.labels.detailsDialogLabel, {
            name: lodgingDetails[activeCabinDetails]?.name,
          })}
        >
          <div className="details-modal__backdrop" onClick={closeCabinDetails} />
          <div className="details-modal__content">
            <header className="details-modal__header">
              <div>
                <p className="details-modal__eyebrow">{content.labels.detailsEyebrow}</p>
                <h3>{lodgingDetails[activeCabinDetails]?.name}</h3>
              </div>
              <button type="button" className="details-modal__close" onClick={closeCabinDetails}>
                {content.labels.close}
              </button>
            </header>
            <ul className="details-modal__list">
              {lodgingDetails[activeCabinDetails]?.details?.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
